import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'
import sorting from '@/lib/utils/sorting'

import {
  transformDepartment,
  transformApiFormErrors,
} from '@/components/cart/helpers/transformHelpers'

import { useRolesStore } from '@/stores/roles'

export const useInventoryThresholdsStore = defineStore('inventory-thresholds', {
  state: () => ({
    modes: {
      new: 'NEW',
      edit: 'EDIT',
      copy: 'COPY',
    },
    isThresholdDepartmentsLoaded: false,
    thresholdDepartments: [],
  }),

  actions: {
    async createInventoryThreshold(thresholdData) {
      try {
        await api.inventoryTracking.createInventoryThreshold(thresholdData)
      } catch (error) {
        const transformedError = new Error('Threshold validation error')
        transformedError.response = {
          data: transformApiFormErrors(error.response.data),
        }
        throw transformedError
      }
    },

    async editInventoryThreshold(thresholdData) {
      try {
        await api.inventoryTracking.updateInventoryThreshold(thresholdData)
      } catch (error) {
        const transformedError = new Error('Threshold validation error')
        transformedError.response = {
          data: transformApiFormErrors(error.response.data),
        }
        throw transformedError
      }
    },

    async validateThreshold(thresholdData) {
      try {
        await api.inventoryTracking.validateThreshold(thresholdData)
        return { ok: true }
      } catch (error) {
        return {
          ok: false,
          errors: transformApiFormErrors(error.response.data),
        }
      }
    },

    async getDepartments(params) {
      const response = await api.inventoryTracking.getDepartments({
        pageSize: 9999,
        ...params,
      })
      const { departments: rawDepartments = [] } = response.data
      const transformDepartments = rawDepartments.map(transformDepartment)
      const sortedDepartments = sorting.sortObjectsListByAlphaAsc(transformDepartments, 'name')
      return sortedDepartments
    },

    async getThresholdDepartments(params) {
      const rolesStore = useRolesStore()
      if (!rolesStore.isUserSuperAdmin && this.isThresholdDepartmentsLoaded) {
        return this.thresholdDepartments
      }
      const departments = await this.getDepartments(params)
      if (!rolesStore.isUserSuperAdmin) {
        this.thresholdDepartments = departments
        this.isThresholdDepartmentsLoaded = true
      }
      return departments
    },

    resetState() {
      this.$reset()
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useInventoryThresholdsStore, import.meta.hot))
}
